// ./games/crash/state/phases/FlyingPhase.ts

import { isStompTick, StompMessage } from '@core/Web/types/StompMessage';
import type { IPhase, PhaseContext } from './IPhase';
// MODIFIED: Removed CrashedPhase import as it's no longer triggered from here
import { DummyTickGenerator } from './DummyTickGenerator'; // NEW: Import the utility class
import { shouldAdvanceLocalGameClock } from '@Web/config/connectionHealth';

export class FlyingPhase implements IPhase {
  private context: PhaseContext;
  private cashoutAttempted: Set<string> = new Set();

  // --- NEW: Use the utility class for state management ---
  private dummyTickGenerator!: DummyTickGenerator;
  // --- (All old dummy tick state and config properties are removed) ---

  constructor(context: PhaseContext) {
    this.context = context;
  }

  onEnter(): void {
    console.log('[Phase] ==> Entering FLYING phase.');
    this.context.store.getState().setPhase('FLYING');
    if (this.context.audio) {
      this.context.audio.play('fly');
    } else {
      console.warn(
        '[FlyingPhase] Audio context not available, skipping fly sound'
      );
    }
    // Reset cashout tracking for new round
    this.cashoutAttempted.clear();

    // --- NEW: Initialize the utility class ---
    // It seamlessly picks up where TakeOffPhase's multiplier left off
    this.dummyTickGenerator = new DummyTickGenerator(
      this.context.store.getState().nowX
    );
  }

  onExit(): void {
    console.log('[Phase] <== Exiting FLYING phase.');
  }

  // --- MODIFIED: Simplified to use the generator class ---
  handleMessage(message: StompMessage): void {
    if (isStompTick(message)) {
      // --- NEW: Ask the generator if this tick should be processed or ignored ---
      if (this.dummyTickGenerator?.shouldProcessRealTick(message.multiplier)) {
        this.context.store.getState().setTick(message.multiplier, message.ts);
      }
    }
    // MODIFIED: Removed the ROUND_SUMMARY handling block.
  }
  // --- END MODIFICATION ---

  // MODIFIED: Simplified to use the generator class
  update(deltaTime: number): void {
    const state = this.context.store.getState();
    // Do not locally advance the multiplier or auto-cashout while disconnected.
    if (!shouldAdvanceLocalGameClock(state.connectionStatus)) {
      return;
    }
    if (!this.dummyTickGenerator) {
      return;
    }

    // --- NEW: Dummy Tick Generation ---
    const currentMultiplier = state.nowX;
    const newDummyMultiplier = this.dummyTickGenerator.update(
      deltaTime,
      currentMultiplier
    );

    if (newDummyMultiplier) {
      // The generator determined a new dummy tick should be set
      this.context.store.getState().setTick(newDummyMultiplier, Date.now());
    }
    // --- END: Dummy Tick Generation ---

    // Check for auto-cashouts on each frame update
    const { bets, nowX, phase, connectionStatus } = state;

    // Only check during FLYING phase
    if (phase !== 'FLYING') return;
    if (!shouldAdvanceLocalGameClock(connectionStatus)) return;

    // Check each bet side for autocashout
    Object.keys(bets).forEach(side => {
      const bet = bets[side as keyof typeof bets];

      // Create unique key for this bet to prevent multiple cashout attempts
      const betKey = `${side}-${bet.betNo}`;

      // Only cashout if:
      // 1. Bet is placed
      // 2. Autocashout is enabled
      // 3. Current multiplier has reached or exceeded the autocashout target
      // 4. We haven't already attempted to cashout this bet
      if (
        bet.status === 'placed' &&
        bet.autoCashoutEnabled &&
        bet.autoCashoutX &&
        nowX >= bet.autoCashoutX &&
        !this.cashoutAttempted.has(betKey)
      ) {
        console.log(`[FlyingPhase] 🎯 Auto-cashout triggered for ${side}:`);
        console.log(`  - Current multiplier: ${nowX}x`);
        console.log(`  - Target multiplier: ${bet.autoCashoutX}x`);
        console.log(`  - Bet status: ${bet.status}`);
        console.log(`  - AutoCashout enabled: ${bet.autoCashoutEnabled}`);

        // Mark this bet as having a cashout attempt
        this.cashoutAttempted.add(betKey);

        // Trigger the cashout immediately
        this.context.controller.cashout(side as any);
      }
    });
  }
}
